import type { SheetTravelStatus } from "./Sheet";

/**
 * The presentation lifecycle of a Silk sheet, as pure functions over
 * (travel status, desired open state, current Silk open state).
 *
 * React-free and Silk-runtime-free by design — `useSheetPresentation` is the
 * only binding, and these run in a plain node test process with no shims.
 */

/** Travel has come to rest. Silk only accepts a new `presented` value here. */
export function isSettled(status: SheetTravelStatus): boolean {
  return status === "idleInside" || status === "idleOutside";
}

/**
 * The `presented` value to hand Silk this render.
 *
 * Silk ignores a `presented` flip made mid-travel and echoes the old value
 * back, so the desired state is queued and released on settle — otherwise a
 * dismissal requested during entry (fast Escape, fast Cancel) is swallowed and
 * the sheet stays open.
 */
export function nextSilkOpen(silkOpen: boolean, desired: boolean, status: SheetTravelStatus): boolean {
  return isSettled(status) ? desired : silkOpen;
}

/**
 * Whether the overlay subtree should render.
 *
 * Stays true on arrival at "idleOutside" so the release can be deferred a
 * paint: Silk tears its layer down in an after-paint effect, and unmounting in
 * the same commit makes it run from React's cleanup instead — after the
 * subtree is detached, where Silk can no longer tell that focus was inside and
 * so never returns it to the opener.
 */
export function isMounted(silkOpen: boolean, desired: boolean, status: SheetTravelStatus): boolean {
  return silkOpen || desired || status !== "idleOutside";
}

/**
 * Whether the exit animation has completed — the edge `onExitComplete` fires
 * on. False when a re-open was queued during the exit, since the surface is
 * about to travel back in rather than having finished leaving.
 */
export function isExitComplete(desired: boolean, status: SheetTravelStatus): boolean {
  return status === "idleOutside" && !desired;
}
